import { Logger } from 'loglevel';
import { z } from 'zod';
import { ENV } from '../config/env';
import { ApiError, type ApiErrorDetails } from '../types/errors';

export class EdiAdminBaseService {
  constructor(protected readonly logger: Logger) {}

  protected readonly apiBaseUrl = ENV.BACK_EDI_URL;

  /**
   * Valide une réponse API désérialisée contre un schéma zod avant de la retourner à l'appelant,
   * au lieu d'un simple cast `as T` qui ne protège pas contre une réponse malformée/évoluée.
   */
  protected parseResponse<T>(schema: z.ZodType<T>, data: unknown, context: string): T {
    const result = schema.safeParse(data);
    if (!result.success) {
      this.logger.error(`Réponse API invalide (${context}):`, {
        issues: result.error.issues.map(issue => `${issue.path.join('.')}: ${issue.message}`),
      });
      throw new ApiError(502, `Réponse invalide du serveur (${context})`);
    }
    return result.data;
  }

  protected async checkEdiApiServiceResponseContentType(response: Response) {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.toLowerCase().startsWith('application/json')) {
      const text = await response.text();
      this.logger.error('Unexpected non-JSON response for client users:', {
        contentType,
        status: response.status,
        preview: text.slice(0, 300),
      });
      throw new Error(`Expected JSON but received: ${contentType}`);
    }
  }

  private async buildEdiApiServiceResponseError(
    response: Response,
    message?: string,
  ): Promise<ApiErrorDetails> {
    const errorText = await response.text();
    let errorMessage: string;

    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.Message || JSON.stringify(errorJson);
    } catch {
      errorMessage = errorText;
    }

    this.logger.error(message ?? 'Error details:', {
      status: response.status,
      statusText: response.statusText,
      errorText: errorMessage,
    });

    return { status: response.status, message: errorMessage };
  }

  protected async handleEdiApiServiceResponseError(response: Response, message?: string) {
    const result = await this.buildEdiApiServiceResponseError(response, message);
    throw new ApiError(result.status, result.message, response.statusText);
  }
}
